import { usePrevious } from "@react-hookz/web";
import { last } from "lodash-es";
import { useCallback, useEffect, useState } from "react";
import { type UUID, uuidv4 } from "../../../../../utils/uuid";
import type { ChannelName } from "../../../../channels/types";
import type { CoPilotResponse } from "../../../../self-driving/types";
import { useCoPilotQueries } from "./use-co-pilot-queries";
import {
  type CoPilot,
  type CoPilotRequest,
  type QueryFn,
  type RefineFn,
  type Selection,
} from "./use-co-pilot-types";

type CoPilotOptions = {
  channel: Exclude<NonNullable<ChannelName>, "whatsapp">;
  mergeTags: string[];
  tone: string;
  history?: CoPilot["history"];
  selection?: Selection;
};

export function useCoPilot({
  channel,
  mergeTags,
  history: initalHistory,
  tone,
  selection,
}: CoPilotOptions): CoPilot {
  const queries = useCoPilotQueries(channel, mergeTags);

  const [history, setHistory] = useState<{ requests: CoPilotRequest[] }>(
    initalHistory ?? { requests: [] },
  );

  const previousSelection = usePrevious(selection);

  useEffect(() => {
    if (selection?.section !== previousSelection?.section) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setHistory(initalHistory ?? { requests: [] });
    }
  }, [selection, previousSelection, initalHistory]);

  const appendRequest = useCallback(
    (prompt: string, response: CoPilotResponse): void => {
      const { requests: existingRequests } = history;

      const responses: CoPilotResponse[] = last(existingRequests)?.responses ?? [];

      const newRequest: CoPilotRequest = {
        prompt,
        responses: [...responses, response],
        tone,
        id: uuidv4(),
      };

      setHistory({
        requests: [...existingRequests, newRequest],
      });
    },
    [history, tone],
  );

  const refine: RefineFn = useCallback(
    async (prompt): Promise<void> => {
      const firstRequest = history.requests.length === 0;
      let promptText;

      switch (prompt) {
        case "shorten":
          promptText = firstRequest
            ? "Please shorten the selected section"
            : "Please shorten the previous suggestion";
          break;
        case "lengthen":
          promptText = firstRequest
            ? "Please lengthen the selected section"
            : "Please lengthen the previous suggestion";
          break;
        case "improve":
          promptText = firstRequest
            ? "Please improve the selected section"
            : "Please improve the previous suggestion";
          break;
        default:
        case "custom":
          promptText = prompt;
          break;
      }

      const context: CoPilotResponse["prompt"] =
        last(history.requests)
          ?.responses.map((r) => r.prompt)
          ?.flat() ?? [];

      let response: CoPilotResponse;
      if (selection && firstRequest) {
        response = await queries.refineSection(selection, promptText);
      } else {
        response = await queries.refineQuery({
          prompt: promptText,
          context,
        });
      }
      if (response) {
        appendRequest(prompt, response);
      }
    },
    [history.requests, selection, queries, appendRequest],
  );

  const getSuggestion = useCallback(
    (id?: UUID): string | void => {
      const request = id ? history.requests.find((r) => r.id === id) : last(history.requests);
      return last(request?.responses)?.response;
    },
    [history.requests],
  );

  const query: QueryFn = useCallback(
    async (prompt) => {
      const response = await queries.query(prompt);
      if (response) {
        appendRequest(prompt.prompt, response);
      }
    },
    [queries, appendRequest],
  );

  return {
    history,
    actions: {
      refine,
      query,
      getSuggestion,
    },
  };
}
